import type {
  ChannelThreadId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  Message,
  MessageWithSupportTicketId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  mergeSupportTicketDetailForCollection,
  useChannelThreadsData,
  useSingleSupportTicketDataCore,
  useSupportTicketData,
  useSupportTicketMessagesData,
  useSupportTicketPresenceData,
  useSupportTicketsData,
  useSupportTicketTimelineEventsData,
  useThreadMessagesData,
} from "@redotech/redo-support-v2/query/support-ticket-data-hooks";
import { supportTicketDetailQueryKey } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import type { SupportTicketThreadCollectionItem } from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
import type { SupportTicketPresenceRecord } from "@redotech/redo-support-v2/realtime/support-ticket-presence-collection";
import { createLiveQueryCollection, eq } from "@tanstack/react-db";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { merchantQueryClient } from "../../react-query-client";
import {
  DEFAULT_PAGE_SIZE,
  fetchSingleSupportTicket,
} from "../db/support-ticket-list-collection";
import { useQueryOptions } from "../filters/filters-context";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import { prefetchSupportRouteModule } from "../support-route-prefetch";

const SUPPORT_TICKET_PREFETCH_STALE_TIME_MS = 5 * 60 * 1000;
const SUPPORT_TICKET_INTENT_PREFETCH_DELAY_MS = 120;
const SUPPORT_TICKET_INTENT_PREFETCH_HOLD_MS = 30 * 1000;

interface PrefetchedSupportTicket {
  cleanupCallbacks: (() => Promise<void>)[];
  cleanupTimer: ReturnType<typeof setTimeout> | null;
  detailStarted: boolean;
  messagesStarted: boolean;
  threadsStarted: boolean;
}

export function useSupportTickets(
  page: number,
  pageSize = DEFAULT_PAGE_SIZE,
): {
  data: SupportTicketListItemResponse[];
  totalCount: number;
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const queryOptions = useQueryOptions();

  return useSupportTicketsData({
    page,
    collections,
    filterGroup: queryOptions.filterGroup,
    sorts: queryOptions.sorts,
    pageSize,
  });
}

export function useSupportTicket(supportTicketId: SupportTicketId): {
  data: SupportTicketListItemResponse | undefined;
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  return useSupportTicketData({
    supportTicketId,
    collections,
    fetchSupportTicket: (id) =>
      fetchSingleSupportTicket(effectApi, id, merchantQueryClient),
  });
}

export function useChannelThreads(supportTicketId: SupportTicketId): {
  data: SupportTicketThreadCollectionItem[];
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  return useChannelThreadsData({ supportTicketId, collections });
}

export function useSupportTicketMessages(supportTicketId: SupportTicketId): {
  data: MessageWithSupportTicketId[];
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  return useSupportTicketMessagesData({ supportTicketId, collections });
}

export function useThreadMessages(
  supportTicketId: SupportTicketId,
  threadIds: ChannelThreadId | readonly ChannelThreadId[],
): { data: Message[]; isLoading: boolean } {
  const collections = useGlobalSupportTicketCollections();
  return useThreadMessagesData({ supportTicketId, threadIds, collections });
}

export function useSupportTicketTimelineEvents(
  supportTicketId: SupportTicketId,
): { data: SupportTicketTimelineEventResponse[]; isLoading: boolean } {
  const collections = useGlobalSupportTicketCollections();
  return useSupportTicketTimelineEventsData({ supportTicketId, collections });
}

export function useSupportTicketPresence(supportTicketId: SupportTicketId): {
  data: SupportTicketPresenceRecord[];
} {
  const collections = useGlobalSupportTicketCollections();
  return useSupportTicketPresenceData({ supportTicketId, collections });
}

export function useSingleSupportTicketData(supportTicketId: SupportTicketId): {
  supportTicket: SupportTicketListItemResponse | undefined;
  threads: SupportTicketThreadCollectionItem[];
  messages: Message[];
  isLoading: boolean;
  supportTicketLoading: boolean;
  transcriptLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  return useSingleSupportTicketDataCore({
    supportTicketId,
    collections,
    fetchSupportTicket: (id) =>
      fetchSingleSupportTicket(effectApi, id, merchantQueryClient),
  });
}

export function usePrefetchSupportTicket(): {
  cancelPendingSupportTicketPrefetch: (
    supportTicketId?: SupportTicketId,
  ) => void;
  prefetchSupportTicket: (supportTicketId: SupportTicketId) => void;
} {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);
  const pendingIntentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pendingIntentIdRef = useRef<SupportTicketId | null>(null);
  const prefetchesRef = useRef(new Map<string, PrefetchedSupportTicket>());

  useEffect(() => {
    const prefetches = prefetchesRef.current;
    return () => {
      if (pendingIntentTimerRef.current) {
        clearTimeout(pendingIntentTimerRef.current);
      }
      for (const prefetch of prefetches.values()) {
        if (prefetch.cleanupTimer) {
          clearTimeout(prefetch.cleanupTimer);
        }
        for (const cleanup of prefetch.cleanupCallbacks) {
          void cleanup();
        }
      }
      prefetches.clear();
    };
  }, []);

  const prefetchNow = useCallback(
    (supportTicketId: SupportTicketId) => {
      const key = String(supportTicketId);
      let prefetch = prefetchesRef.current.get(key);
      if (!prefetch) {
        prefetch = {
          cleanupCallbacks: [],
          cleanupTimer: null,
          detailStarted: false,
          messagesStarted: false,
          threadsStarted: false,
        };
        prefetchesRef.current.set(key, prefetch);
      }

      if (prefetch.cleanupTimer) {
        clearTimeout(prefetch.cleanupTimer);
      }
      prefetch.cleanupTimer = setTimeout(() => {
        const currentPrefetch = prefetchesRef.current.get(key);
        if (!currentPrefetch) {
          return;
        }
        for (const cleanup of currentPrefetch.cleanupCallbacks) {
          void cleanup();
        }
        prefetchesRef.current.delete(key);
      }, SUPPORT_TICKET_INTENT_PREFETCH_HOLD_MS);

      void prefetchSupportRouteModule().catch(() => undefined);

      if (!prefetch.detailStarted) {
        prefetch.detailStarted = true;
        void merchantQueryClient
          .prefetchQuery({
            queryKey: supportTicketDetailQueryKey(
              supportTicketId,
              collections.supportTicketsList.queryScope,
            ),
            queryFn: async () => {
              const supportTicket = await fetchSingleSupportTicket(
                effectApi,
                supportTicketId,
                merchantQueryClient,
              );
              const existing =
                collections.supportTicketsList.collection.get(supportTicketId);
              collections.supportTicketsList.writeItem(
                mergeSupportTicketDetailForCollection(supportTicket, existing),
              );
              return supportTicket;
            },
            staleTime: SUPPORT_TICKET_PREFETCH_STALE_TIME_MS,
          })
          .catch(() => undefined);
      }

      if (!prefetch.threadsStarted) {
        prefetch.threadsStarted = true;
        const threadsQuery = createLiveQueryCollection((q) =>
          q
            .from({ thread: collections.threads })
            .where(({ thread }) => eq(thread.supportTicketId, supportTicketId))
            .orderBy(({ thread }) => thread.createdAt, "asc"),
        );
        prefetch.cleanupCallbacks.push(() => threadsQuery.cleanup());
        void threadsQuery.preload().catch(() => undefined);
      }

      if (!prefetch.messagesStarted) {
        prefetch.messagesStarted = true;
        const messagesQuery = createLiveQueryCollection((q) =>
          q
            .from({ message: collections.messages })
            .where(({ message }) =>
              eq(message.supportTicketId, supportTicketId),
            )
            .orderBy(({ message }) => message.sentAt, "asc"),
        );
        prefetch.cleanupCallbacks.push(() => messagesQuery.cleanup());
        void messagesQuery.preload().catch(() => undefined);
      }
    },
    [collections, effectApi],
  );

  const cancelPendingSupportTicketPrefetch = useCallback(
    (supportTicketId?: SupportTicketId) => {
      if (
        supportTicketId !== undefined &&
        pendingIntentIdRef.current !== supportTicketId
      ) {
        return;
      }
      if (pendingIntentTimerRef.current) {
        clearTimeout(pendingIntentTimerRef.current);
        pendingIntentTimerRef.current = null;
      }
      pendingIntentIdRef.current = null;
    },
    [],
  );

  const prefetchSupportTicket = useCallback(
    (supportTicketId: SupportTicketId) => {
      pendingIntentIdRef.current = supportTicketId;
      if (pendingIntentTimerRef.current) {
        clearTimeout(pendingIntentTimerRef.current);
      }
      pendingIntentTimerRef.current = setTimeout(() => {
        if (pendingIntentIdRef.current === supportTicketId) {
          prefetchNow(supportTicketId);
        }
      }, SUPPORT_TICKET_INTENT_PREFETCH_DELAY_MS);
    },
    [prefetchNow],
  );

  return useMemo(
    () => ({ cancelPendingSupportTicketPrefetch, prefetchSupportTicket }),
    [cancelPendingSupportTicketPrefetch, prefetchSupportTicket],
  );
}
